import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigProvider, Spin, notification, theme } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  getLocaleForLanguage,
  localizeTournamentName,
  normalizeLanguage,
  translatePair,
} from "../../services/language";
import { setLanguage as setI18nLanguage, t as tKey } from "../../i18n";
import { resolveAvatarSrc } from "../../utils/avatarUrl";

const formatDateLabel = (value, language) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "-";
  return date.toLocaleString(getLocaleForLanguage(language), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDurationLabel = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatLongDurationLabel = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return formatDurationLabel(safeSeconds);
};

const outcomeColorMap = {
  win: "border-[#2b8b4e] bg-[#13372a] text-[#86ffb5]",
  loss: "border-[#8b2b2b] bg-[#3b1a1a] text-[#ff9f9f]",
  draw: "border-[#355a9c] bg-[#182946] text-[#a7c5ff]",
};

const statusColorMap = {
  online: "border-[#4fd0ff] bg-[#11406a] text-[#dff5ff]",
  in_match: "border-[#6dff93] bg-[#18492d] text-[#dcffe8]",
  offline: "border-[#5c6f8e] bg-[#243753] text-[#cfdef5]",
};

const FriendProfile = () => {
  const navigate = useNavigate();
  const { playerCode } = useParams();
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const t = useCallback((es, en) => translatePair(language, es, en), [language]);
  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);

  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoadError, setProfileLoadError] = useState("");
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);

  const loadProfile = useCallback(async () => {
    const normalizedCode = String(playerCode || "").trim();
    if (!normalizedCode) return;

    try {
      setLoading(true);
      setProfileLoadError("");
      const response = await axios.get(
        `/friends/profile/${encodeURIComponent(normalizedCode)}`,
        {
          params: { limit: 20 },
        }
      );
      setProfileData(response?.data || null);
    } catch (error) {
      const resolvedErrorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t("No se pudo cargar el perfil del amigo.", "Could not load friend profile.");
      notification.error({
        message: t("Error de perfil", "Profile error"),
        description: resolvedErrorMessage,
      });
      setProfileLoadError(String(resolvedErrorMessage || "").trim());
      setProfileData(null);
    } finally {
      setLoading(false);
    }
  }, [playerCode, t]);

  useEffect(() => {
    loadProfile().catch((error) => console.log(error));
  }, [loadProfile]);

  const profile = profileData?.profile || {};
  const stats = useMemo(() => profileData?.stats || {}, [profileData]);
  const matches = Array.isArray(profileData?.matches) ? profileData.matches : [];
  const totalMatches = Number(stats?.totalMatches || 0);
  const totalWins = Number(stats?.wins || 0);
  const totalLosses = Number(stats?.losses || 0);
  const averageDurationSeconds = Number(stats?.averageDurationSeconds || 0);
  const winRate = Number(stats?.winRate || 0);
  const totalTimePlayedSeconds = Number(stats?.totalTimePlayedSeconds || 0);
  const currentWinStreak = Number(stats?.currentWinStreak || 0);
  const bestWinStreak = Number(stats?.bestWinStreak || 0);
  const capturedBlocks = Number(stats?.capturedBlocks || 0);
  const specialCaptures = Number(stats?.specialCaptures || 0);
  const consecutiveDaysStreak = Number(stats?.consecutiveDaysStreak || 0);

  const cards = useMemo(
    () => [
      {
        key: "matches",
        label: t("Partidas", "Matches"),
        value: totalMatches,
        className: "border-[#25508c] bg-[#0d2344]",
        labelClass: "text-[#8cc6ff]",
      },
      {
        key: "wins",
        label: t("Victorias", "Wins"),
        value: totalWins,
        className: "border-[#2b8b4e] bg-[#13372a]",
        labelClass: "text-[#86ffb5]",
      },
      {
        key: "losses",
        label: t("Derrotas", "Losses"),
        value: totalLosses,
        className: "border-[#8b2b2b] bg-[#3b1a1a]",
        labelClass: "text-[#ff9f9f]",
      },
      {
        key: "avg",
        label: t("Tiempo medio", "Avg duration"),
        value: formatDurationLabel(averageDurationSeconds),
        className: "border-[#5c4ab6] bg-[#1f2350]",
        labelClass: "text-[#bab3ff]",
      },
      {
        key: "rate",
        label: t("Win rate", "Win rate"),
        value: `${winRate.toFixed(1)}%`,
        className: "border-[#7a5a1f] bg-[#3a2a12]",
        labelClass: "text-[#ffe09a]",
      },
      {
        key: "totalTime",
        label: t("Tiempo total jugado", "Total play time"),
        value: formatLongDurationLabel(totalTimePlayedSeconds),
        className: "border-[#336b7a] bg-[#112f3a]",
        labelClass: "text-[#9cecff]",
      },
      {
        key: "currentStreak",
        label: t("Racha de victorias", "Win streak"),
        value: currentWinStreak,
        className: "border-[#2f8b76] bg-[#14392f]",
        labelClass: "text-[#9fffd8]",
      },
      {
        key: "bestStreak",
        label: t("Mejor racha", "Best streak"),
        value: bestWinStreak,
        className: "border-[#4f7d2f] bg-[#293a14]",
        labelClass: "text-[#cfff9f]",
      },
      {
        key: "capturedBlocks",
        label: t("Bloques capturados", "Captured blocks"),
        value: capturedBlocks,
        className: "border-[#8b5a2b] bg-[#3b2613]",
        labelClass: "text-[#ffc99f]",
      },
      {
        key: "capturedSpecial",
        label: t("Especiales capturadas", "Special captures"),
        value: specialCaptures,
        className: "border-[#7a2b8b] bg-[#35113e]",
        labelClass: "text-[#f4b0ff]",
      },
      {
        key: "dailyStreak",
        label: t("Racha de dias", "Day streak"),
        value: consecutiveDaysStreak,
        extra: t("Dias seguidos jugando", "Consecutive days playing"),
        className: "border-[#2b5f8b] bg-[#112a3f]",
        labelClass: "text-[#9fd7ff]",
      },
    ],
    [
      averageDurationSeconds,
      bestWinStreak,
      capturedBlocks,
      consecutiveDaysStreak,
      currentWinStreak,
      specialCaptures,
      t,
      totalLosses,
      totalMatches,
      totalTimePlayedSeconds,
      totalWins,
      winRate,
    ]
  );

  const onlineRates = useMemo(() => {
    const fromApi = stats?.onlineModeWinRates || {};
    return [
      {
        key: "free",
        label: "Ciencia",
        value: Number(fromApi?.free?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.free?.matches || 0),
        wins: Number(fromApi?.free?.wins || 0),
        losses: Number(fromApi?.free?.losses || 0),
        draws: Number(fromApi?.free?.draws || 0),
        className: "border-[#3b80d8] bg-[#132c4a] text-[#c4e2ff]",
      },
      {
        key: "cienzar",
        label: "Cienzar",
        value: Number(fromApi?.cienzar?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.cienzar?.matches || 0),
        wins: Number(fromApi?.cienzar?.wins || 0),
        losses: Number(fromApi?.cienzar?.losses || 0),
        draws: Number(fromApi?.cienzar?.draws || 0),
        className: "border-[#2f9658] bg-[#143824] text-[#bfffd6]",
      },
      {
        key: "sciencevsazar",
        label: "Contrazar",
        value: Number(fromApi?.sciencevsazar?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.sciencevsazar?.matches || 0),
        wins: Number(fromApi?.sciencevsazar?.wins || 0),
        losses: Number(fromApi?.sciencevsazar?.losses || 0),
        draws: Number(fromApi?.sciencevsazar?.draws || 0),
        className: "border-[#b9414c] bg-[#3d1018] text-[#ffc2ca]",
      },
      {
        key: "dice",
        label: "Azar",
        value: Number(fromApi?.dice?.winRate || 0).toFixed(1),
        matches: Number(fromApi?.dice?.matches || 0),
        wins: Number(fromApi?.dice?.wins || 0),
        losses: Number(fromApi?.dice?.losses || 0),
        draws: Number(fromApi?.dice?.draws || 0),
        className: "border-[#b98a1f] bg-[#3f2d11] text-[#ffe3a6]",
      },
    ];
  }, [stats]);

  const statusLabel = useMemo(
    () => ({
      online: t("En linea", "Online"),
      in_match: t("En partida", "In match"),
      offline: t("Desconectado", "Offline"),
    }),
    [t]
  );

  const outcomeLabel = useMemo(
    () => ({
      win: t("Victoria", "Win"),
      loss: tKey("DERROTA"),
      draw: t("Empate", "Draw"),
    }),
    [t]
  );

  const profileStatus = String(profile?.status || "offline")
    .trim()
    .toLowerCase();
  const profileStatusClass = statusColorMap[profileStatus] || statusColorMap.offline;
  const profileAvatarUrl = resolveAvatarSrc(profile?.avatarUrl || profile?.avatar || profile?.image || "");
  const profileAvatarInitial =
    String(profile?.displayName || profile?.firstName || profile?.playerCode || "?")
      .trim()
      .charAt(0)
      .toUpperCase() || "?";

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [profileAvatarUrl]);

  const handleOpenMatchReplay = useCallback(
    (match) => {
      const matchId = String(match?.id || "").trim();
      if (!matchId || !match?.hasReplay) {
        notification.warning({
          message: t("Replay no disponible", "Replay unavailable"),
          description: t(
            "Esta partida no tiene replay guardado.",
            "This match has no saved replay."
          ),
        });
        return;
      }

      navigate("/game/history", {
        state: {
          openReplayRequestKey: `friend-profile-${matchId}-${Date.now()}`,
          openReplayMatch: {
            id: matchId,
            hasReplay: true,
            replaySteps: Math.max(0, Number(match?.replaySteps) || 0),
            tournamentId: String(match?.tournamentId || "").trim(),
            roomId: String(match?.roomId || "").trim(),
            tournamentName: String(match?.tournamentName || "Room").trim(),
            mode: String(match?.mode || "").trim(),
            modeLabel: String(match?.modeLabel || "").trim(),
            durationSeconds: Math.max(0, Number(match?.durationSeconds) || 0),
            endedAt: match?.endedAt || null,
            participants: Array.isArray(match?.participants) ? match.participants : [],
          },
        },
      });
    },
    [navigate, t]
  );

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: "rgb(80, 80, 200)" },
        algorithm: theme.darkAlgorithm,
      }}
    >
      <div className="relative h-full p-4 text-white md:p-6">
        <div className="flex h-full min-h-0 flex-col gap-4 rounded-xl border border-[#24406f] bg-[#070f24e6] p-4 shadow-[0_0_30px_rgba(0,0,0,0.35)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#f3c74f] bg-[#171104] text-lg font-black text-[#ffd86b] shadow-[0_0_18px_rgba(243,199,79,0.22)]">
                {profileAvatarUrl && !avatarLoadFailed ? (
                  <img
                    src={profileAvatarUrl}
                    alt={profile?.displayName || "Jugador"}
                    className="h-full w-full object-cover"
                    onError={() => setAvatarLoadFailed(true)}
                  />
                ) : (
                  profileAvatarInitial
                )}
              </div>
              <div className="min-w-0">
              <div className="text-lg font-black uppercase tracking-wide text-[#9ed6ff]">
                {t("Perfil de amigo", "Friend profile")}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[#9ab2d8]">
                <span className="font-semibold text-white">{profile?.displayName || "-"}</span>
                <span>·</span>
                <span>{profile?.playerCode || "-"}</span>
                <span
                  className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${profileStatusClass}`}
                >
                  {statusLabel[profileStatus] || statusLabel.offline}
                </span>
              </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate("/game/friends")}
                className="rounded border border-[#3ea6ff99] bg-[#0d2342] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#9cd8ff] transition hover:border-[#7ee5ff] hover:text-white"
              >
                {t("Volver a Amigos", "Back to Friends")}
              </button>
              <button
                type="button"
                onClick={() => loadProfile()}
                className="rounded border border-[#4fc3ff88] bg-[#11305c] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
              >
                {tKey("ACTUALIZAR")}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spin size="large" />
            </div>
          ) : profileLoadError ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="w-full max-w-xl rounded border border-[#7f3f3f] bg-[#2f1414] p-4 text-sm text-[#ffcdcd]">
                {profileLoadError}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                {cards.map((card) => (
                  <div key={card.key} className={`rounded border p-4 ${card.className}`}>
                    <div className={`text-[10px] uppercase tracking-[0.16em] ${card.labelClass}`}>
                      {card.label}
                    </div>
                    <div className="mt-1 text-4xl font-black text-white">{card.value}</div>
                    {card.extra ? (
                      <div className="mt-1 text-xs text-[#ffd67f]">{card.extra}</div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="rounded border border-[#34587f] bg-[#0b1c36] p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9ed6ff]">
                  {t("Win Rate por modo online", "Online mode win rate")}
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                  {onlineRates.map((modeRate) => (
                    <div
                      key={modeRate.key}
                      className={`rounded border p-3 ${modeRate.className}`}
                    >
                      <div className="text-xs font-semibold uppercase tracking-[0.14em]">
                        {modeRate.label}
                      </div>
                      <div className="mt-1 text-2xl font-black">{modeRate.value}%</div>
                      <div className="mt-1 text-[11px] text-[#d9e6ff]">
                        W {modeRate.wins} · L {modeRate.losses} · D {modeRate.draws} · M{" "}
                        {modeRate.matches}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto rounded border border-[#324f7a] bg-[#0b1d39] p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#9ed6ff]">
                  {t("Partidas recientes", "Recent matches")}
                </div>
                {matches.length === 0 ? (
                  <div className="rounded border border-dashed border-[#38557f] bg-[#091834] p-3 text-sm text-[#93b4dc]">
                    {t("Este jugador aún no tiene partidas registradas.", "This player has no recorded matches yet.")}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {matches.map((match) => {
                      const outcome = String(match?.outcome || "draw")
                        .trim()
                        .toLowerCase();
                      const outcomeClass = outcomeColorMap[outcome] || outcomeColorMap.draw;
                      return (
                        <div
                          key={String(match?.id || `${match?.tournamentId}:${match?.roomId}`)}
                          className="rounded border border-[#3a5f94] bg-[#10284d] p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-white">
                              {localizeTournamentName(language, match?.tournamentName) || "Room"}
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${outcomeClass}`}
                              >
                                {outcomeLabel[outcome] || outcomeLabel.draw}
                              </span>
                              {match?.hasReplay ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenMatchReplay(match)}
                                  className="rounded border border-[#57c4ff] bg-[#114a7b] px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-[#dff5ff] transition hover:border-[#7fe0ff] hover:text-white"
                                >
                                  {t("Reproducir", "Replay")}
                                </button>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#a7c5ef]">
                            <span>{tKey("MODE")}: {String(match?.modeLabel || "-").toUpperCase()}</span>
                            <span>{tKey("TIEMPO")}: {Number(match?.havingTime || 0)} min</span>
                            <span>{tKey("DURATION")}: {formatDurationLabel(match?.durationSeconds || 0)}</span>
                            <span>{t("Finalizó", "Ended")}: {formatDateLabel(match?.endedAt, language)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
};

export default FriendProfile;
